import { Routes } from '@angular/router';

import { authGuard } from './core/auth/guards/auth.guard';
import { publicOnlyGuard } from './core/auth/guards/public-only.guard';
import { roleGuard } from './core/auth/guards/role.guard';
import { AuthRole } from './features/auth/models/auth-role.enum';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/public-layout/public-layout.component').then(
        (m) => m.PublicLayoutComponent,
      ),
    children: [
      {
        path: 'login',
        canActivate: [publicOnlyGuard],
        loadComponent: () =>
          import('./features/auth/pages/login-page/login-page.component').then(
            (m) => m.LoginPageComponent,
          ),
      },
      {
        path: 'cadastro',
        canActivate: [publicOnlyGuard],
        loadComponent: () =>
          import('./features/auth/pages/register-citizen-page/register-citizen-page.component').then(
            (m) => m.RegisterCitizenPageComponent,
          ),
      },
      {
        path: 'recuperacao-senha',
        loadComponent: () =>
          import('./features/demo/auth/pages/register-demo-page/register-demo-page.component').then(
            (m) => m.RegisterDemoPageComponent,
          ),
      },
      {
        path: 'consentimento',
        loadComponent: () =>
          import('./features/citizen/consent/pages/public-consent-page/public-consent-page.component').then(
            (m) => m.PublicConsentPageComponent,
          ),
      },
      {
        path: 'ciencia',
        loadComponent: () =>
          import('./features/citizen/consent/pages/public-consent-page/public-consent-page.component').then(
            (m) => m.PublicConsentPageComponent,
          ),
      },
    ],
  },
  {
    path: 'acesso-negado',
    loadComponent: () =>
      import('./layouts/public-layout/public-layout.component').then(
        (m) => m.PublicLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/access-denied/pages/access-denied-page/access-denied-page.component').then(
            (m) => m.AccessDeniedPageComponent,
          ),
      },
    ],
  },
  {
    path: 'solicitacoes',
    canActivate: [authGuard, roleGuard],
    data: { roles: [AuthRole.Admin, AuthRole.Senatran, AuthRole.Company, AuthRole.Citizen] },
    loadComponent: () =>
      import('./layouts/private-layout/private-layout.component').then(
        (m) => m.PrivateLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/consent-requests/pages/consent-request-list-page/consent-request-list-page.component').then(
            (m) => m.ConsentRequestListPageComponent,
          ),
      },
      {
        path: 'pgcc/:operationType/:pgccId',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Citizen] },
        loadComponent: () =>
          import('./features/consent-requests/pages/citizen-pgcc-detail-page/citizen-pgcc-detail-page.component').then(
            (m) => m.CitizenPgccDetailPageComponent,
          ),
      },
      {
        path: ':consentRequestId',
        loadComponent: () =>
          import('./features/consent-requests/pages/consent-request-detail-page/consent-request-detail-page.component').then(
            (m) => m.ConsentRequestDetailPageComponent,
          ),
      },
    ],
  },
  {
    path: 'perfil',
    canActivate: [authGuard, roleGuard],
    data: { roles: [AuthRole.Admin, AuthRole.Senatran, AuthRole.Company, AuthRole.Citizen] },
    loadComponent: () =>
      import('./layouts/private-layout/private-layout.component').then(
        (m) => m.PrivateLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/user-profile/pages/user-profile-page/user-profile-page.component').then(
            (m) => m.UserProfilePageComponent,
          ),
      },
    ],
  },
  {
    path: 'anuentes',
    canActivate: [authGuard, roleGuard],
    data: { roles: [AuthRole.Admin, AuthRole.Senatran, AuthRole.Company] },
    loadComponent: () =>
      import('./layouts/private-layout/private-layout.component').then(
        (m) => m.PrivateLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/company-anuents/pages/company-anuent-list-page/company-anuent-list-page.component').then(
            (m) => m.CompanyAnuentListPageComponent,
          ),
      },
      {
        path: 'novo',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/company-anuents/pages/company-anuent-create-page/company-anuent-create-page.component').then(
            (m) => m.CompanyAnuentCreatePageComponent,
          ),
      },
      {
        path: ':anuentId/editar',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/company-anuents/pages/company-anuent-detail-page/company-anuent-detail-page.component').then(
            (m) => m.CompanyAnuentDetailPageComponent,
          ),
      },
      {
        path: ':anuentId',
        loadComponent: () =>
          import('./features/company-anuents/pages/company-anuent-detail-page/company-anuent-detail-page.component').then(
            (m) => m.CompanyAnuentDetailPageComponent,
          ),
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [authGuard, roleGuard],
    data: { roles: [AuthRole.Admin, AuthRole.Senatran] },
    loadComponent: () =>
      import('./layouts/private-layout/private-layout.component').then(
        (m) => m.PrivateLayoutComponent,
      ),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin, AuthRole.Senatran] },
        loadComponent: () =>
          import('./features/admin/dashboard/pages/admin-dashboard-page/admin-dashboard-page.component').then(
            (m) => m.AdminDashboardPageComponent,
          ),
      },
      {
        path: 'empresas',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin, AuthRole.Senatran] },
        loadComponent: () =>
          import('./features/admin/companies/pages/admin-company-list-page/admin-company-list-page.component').then(
            (m) => m.AdminCompanyListPageComponent,
          ),
      },
      {
        path: 'empresas/nova',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/admin/companies/pages/admin-company-create-page/admin-company-create-page.component').then(
            (m) => m.AdminCompanyCreatePageComponent,
          ),
      },
      {
        path: 'empresas/:companyId/editar',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/admin/companies/pages/admin-company-detail-page/admin-company-detail-page.component').then(
            (m) => m.AdminCompanyDetailPageComponent,
          ),
      },
      {
        path: 'empresas/:companyId',
        canActivate: [roleGuard],
        data: { roles: [AuthRole.Admin, AuthRole.Senatran] },
        loadComponent: () =>
          import('./features/admin/companies/pages/admin-company-view-page/admin-company-view-page').then(
            (m) => m.AdminCompanyViewPage,
          ),
      },
      {
        path: 'usuarios',
        canActivate: [authGuard, roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/admin/platform-users/pages/platform-user-list-page/platform-user-list-page').then(
            (m) => m.PlatformUserListPage,
          ),
      },
      {
        path: 'usuarios/novo',
        canActivate: [authGuard, roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/admin/platform-users/pages/platform-user-create-page/platform-user-create-page').then(
            (m) => m.PlatformUserCreatePage,
          ),
      },
      {
        path: 'usuarios/:userId/editar',
        canActivate: [authGuard, roleGuard],
        data: { roles: [AuthRole.Admin] },
        loadComponent: () =>
          import('./features/admin/platform-users/pages/platform-user-edit-page/platform-user-edit-page').then(
            (m) => m.PlatformUserEditPage,
          ),
      },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/fallback/pages/not-found-page/not-found-page.component').then(
            (m) => m.NotFoundPageComponent,
          ),
      },
    ],
  },
  {
    path: 'company',
    canActivate: [authGuard, roleGuard],
    data: { roles: [AuthRole.Company] },
    loadComponent: () =>
      import('./layouts/private-layout/private-layout.component').then(
        (m) => m.PrivateLayoutComponent,
      ),
    children: [
      {
        path: '',
        redirectTo: 'consentimentos',
        pathMatch: 'full',
      },
      // {
      //   path: 'dashboard',
      //   loadComponent: () =>
      //     import('./features/company/dashboard/pages/company-dashboard-page/company-dashboard-page.component').then(
      //       (m) => m.CompanyDashboardPageComponent,
      //     ),
      // },
      {
        path: 'empresa',
        loadComponent: () =>
          import('./features/company/profile/pages/company-profile-page/company-profile-page.component').then(
            (m) => m.CompanyProfilePageComponent,
          ),
      },
      {
        path: '**',
        loadComponent: () =>
          import('./pages/fallback/pages/not-found-page/not-found-page.component').then(
            (m) => m.NotFoundPageComponent,
          ),
      },
    ],
  },
  {
    path: 'app',
    redirectTo: 'admin/dashboard',
  },
  {
    path: '**',
    redirectTo: 'admin/dashboard',
  },
];
